import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { ChangeEvent, FocusEvent, MouseEvent } from "react";
import { createPortal } from "react-dom";
import { format } from "date-fns";
import { FaCalendarAlt } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import U2000Label from "../label/U2000Label";
import type { U2000FormStateHandlers } from "../../../reducer/formReducer/U2000FormStateHandlers";
import { U2000FormStateEnumState } from "../../../reducer/formReducer/U2000FormState";
import { getNestedValue, setNestedValue } from "../../../tools/U2000ObjectUtils";
import { parseDateWithFormat, validateDate } from "../../../tools/U2000DateUtils";
import U2000CalendarPopup, { type CalendarLabels, type CalendarPosition } from "../calendar/U2000CalendarPopup";
import U2000InvalidFeedback from "../feedback/U2000InvalidFeedback";
import { u2000SelectInputContentsOnFocus } from "../../../tools/U2000FormInputUtils";

const CALENDAR_POPUP_WIDTH = 320;
const CALENDAR_POPUP_HEIGHT_EST = 400;
const CALENDAR_VIEWPORT_GAP = 8;

function computeCalendarViewportPosition(anchorEl: HTMLElement, position: CalendarPosition): { top: number; left: number } {
  const rect = anchorEl.getBoundingClientRect();
  const spaceBelow = window.innerHeight - rect.bottom - CALENDAR_VIEWPORT_GAP;
  const spaceAbove = rect.top - CALENDAR_VIEWPORT_GAP;
  let openAbove = false;
  if (position === "top") {
    openAbove = true;
  } else if (position === "auto") {
    openAbove = spaceBelow < CALENDAR_POPUP_HEIGHT_EST && spaceAbove > spaceBelow;
  }
  let top = openAbove ? rect.top - CALENDAR_POPUP_HEIGHT_EST - CALENDAR_VIEWPORT_GAP : rect.bottom + CALENDAR_VIEWPORT_GAP;
  if (top < CALENDAR_VIEWPORT_GAP) top = CALENDAR_VIEWPORT_GAP;
  const maxTop = window.innerHeight - CALENDAR_POPUP_HEIGHT_EST - CALENDAR_VIEWPORT_GAP;
  if (top > maxTop) top = Math.max(CALENDAR_VIEWPORT_GAP, maxTop);
  let left = rect.left;
  const maxLeft = window.innerWidth - CALENDAR_POPUP_WIDTH - CALENDAR_VIEWPORT_GAP;
  if (left > maxLeft) left = Math.max(CALENDAR_VIEWPORT_GAP, maxLeft);
  if (left < CALENDAR_VIEWPORT_GAP) left = CALENDAR_VIEWPORT_GAP;
  return { top, left };
}

interface U2000InputCalendarCustomProps {
  formStateHandlers: U2000FormStateHandlers<any>;
  fieldName: string;
  label?: string;
  readonly?: boolean;
  required?: boolean;
  dateFormat?: string;
  placeholder?: string;
  min?: string;
  max?: string;
  /** Emplacement du popup calendrier : "top" | "bottom" | "auto" (détection selon l'espace). */
  popupPosition?: CalendarPosition;
  /** z-index du popup (dialogues, tableaux : préférer une valeur élevée, ex. 9999). */
  popupZIndex?: number;
  /**
   * Si true (défaut), le calendrier est rendu dans document.body en position fixed pour éviter la troncature
   * dans les tableaux / zones scroll (overflow).
   */
  popoverInPortal?: boolean;
  /**
   * Contenu affiché à droite du champ sur la même ligne (ex. case à cocher), aligné verticalement au centre avec l’input.
   * Le message d’erreur reste sous la ligne input + contenu.
   */
  inlineEnd?: React.ReactNode;
  /**
   * Appelé après sélection d'une date (popup). Si fourni, remplace le restore-focus sur le champ date
   * (ex. focuser le libellé adjacent dans une grille).
   */
  onAfterSelect?: () => void;
  /** Grille compacte U3129 (h-8) — coque calendrier ; l’input interne ignore les surcharges [&_input] du tableau. */
  compactGrid?: boolean;
}

// Hauteur fixe alignée sur les tableaux U3129 ([&_input]:h-[46px]) : pas de py-3 sur le flex (sinon + padding = zone plus haute / effet « étiré »).
const getCustomCalendarClasses = (
  formStateHandlers: U2000FormStateHandlers<any>,
  fieldName: string,
  isFocused: boolean,
  readonly?: boolean,
  compactGrid?: boolean,
) => {
  const shellHeight = compactGrid ? "h-8 min-h-8 max-h-8 px-2" : "h-[46px] shrink-0 px-4";
  // BL-0349 : un seul fond par état (pas bg-surface + bg-muted)
  const baseClasses = `flex items-center w-full ${shellHeight} py-0 text-sm border rounded-lg transition-all duration-200 cursor-pointer`;
  const isDisabled = readonly || formStateHandlers.getFormState() === U2000FormStateEnumState.READ;
  const hasError = (formStateHandlers.getTouched()[fieldName] && formStateHandlers.getErrors()[fieldName]) || (formStateHandlers.isSubmitted() && formStateHandlers.getErrors()[fieldName]);

  if (isDisabled) {
    return `${baseClasses} bg-muted text-gray-500 border-gray-200 cursor-not-allowed`;
  }

  if (hasError) {
    return `${baseClasses} bg-surface border-red-300 focus-within:border-red-500 focus-within:ring-2 focus-within:ring-red-500/20 text-gray-900`;
  }

  if (isFocused) {
    return `${baseClasses} bg-surface border-blue-500 ring-2 ring-blue-500/20 text-gray-900`;
  }

  return `${baseClasses} bg-surface border-gray-300 hover:border-gray-400 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500/20 text-gray-900`;
};

const U2000InputCalendarCustom: React.FC<U2000InputCalendarCustomProps> = ({
  formStateHandlers,
  fieldName,
  label,
  readonly = false,
  required = false,
  dateFormat = "yyyy-MM-dd",
  placeholder,
  min,
  max,
  popupPosition = "bottom",
  popupZIndex = 9999,
  popoverInPortal = true,
  inlineEnd,
  onAfterSelect,
  compactGrid = false,
}) => {
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [isOpenCalendar, setIsOpenCalendar] = useState<boolean>(false);
  const [isFocused, setIsFocused] = useState(false);
  const [inputValue, setInputValue] = useState<string>("");
  const [viewportFixed, setViewportFixed] = useState<{ top: number; left: number } | null>(null);

  const parentRef = useRef<HTMLDivElement>(null);
  const popoverWrapRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  /** Évite qu'un blur-sm différé (clic popup) écrase une date venant d'être sélectionnée. */
  const skipBlurCommitRef = useRef(false);
  const { t } = useTranslation();

  /**
   * Écrit la date dans le form SANS passer par onChange (évite double dispatch).
   * Met à jour UNIQUEMENT l'erreur de ce champ (pas validateForm global → flash des libellés / isValid).
   * @returns le snapshot écrit, ou null si pas de data / inchangé.
   */
  function writeCivilDateToForm(ymd: string | null): Record<string, unknown> | null {
    const currentValue = formStateHandlers.getData();
    if (currentValue == null) return null;
    const prevYmd = getNestedValue(currentValue, fieldName);
    const same = String(prevYmd ?? "") === String(ymd ?? "");

    const syncFieldErrorOnly = (row: Record<string, unknown>) => {
      const validate = formStateHandlers.getBusinessHandlers?.()?.validateForm;
      if (!validate || !formStateHandlers.setErrors) return;
      const fieldError = validate(row)?.[fieldName];
      const prevErrors = formStateHandlers.getErrors() || {};
      const prevFieldError = prevErrors[fieldName];
      if ((fieldError ?? null) === (prevFieldError ?? null)) return;
      const nextErrors = { ...prevErrors };
      if (fieldError) {
        nextErrors[fieldName] = fieldError;
      } else {
        delete nextErrors[fieldName];
      }
      formStateHandlers.setErrors(nextErrors);
    };

    if (same) {
      // Valeur déjà en form : purge seulement une erreur périmée sur ce champ.
      syncFieldErrorOnly(currentValue as Record<string, unknown>);
      return currentValue as Record<string, unknown>;
    }
    const cloned =
      typeof structuredClone === "function"
        ? structuredClone(currentValue)
        : JSON.parse(JSON.stringify(currentValue));
    setNestedValue(cloned, fieldName, ymd);
    formStateHandlers.setData(cloned);
    syncFieldErrorOnly(cloned);
    return cloned;
  }

  // Préparer les labels pour le calendrier popup
  const calendarLabels: CalendarLabels = {
    monthNames: [
      t("lblCalendarJan"),
      t("lblCalendarFeb"),
      t("lblCalendarMar"),
      t("lblCalendarApr"),
      t("lblCalendarMay"),
      t("lblCalendarJun"),
      t("lblCalendarJul"),
      t("lblCalendarAug"),
      t("lblCalendarSep"),
      t("lblCalendarOct"),
      t("lblCalendarNov"),
      t("lblCalendarDec"),
    ],
    dayNames: [t("lblCalendarSun"), t("lblCalendarMon"), t("lblCalendarTue"), t("lblCalendarWed"), t("lblCalendarThu"), t("lblCalendarFri"), t("lblCalendarSat")],
    today: t("lblCalendarToday"),
    clear: t("lblCalendarClear"),
  };

  function handleCalendarClick() {
    if (readonly || formStateHandlers.getFormState() === U2000FormStateEnumState.READ) return;
    if (!isOpenCalendar) setIsFocused(true);
    setIsOpenCalendar(!isOpenCalendar);
  }

  const validateDateStr = (dateStr: string): boolean => {
    return validateDate(dateStr, dateFormat);
  };

  function handleInputChangeDate(val: string) {
    const v = val.replace(/[^0-9/\-]/g, "");
    setInputValue(v);
    // Dès qu'une date civile complète est saisie, synchroniser le form (évite DOM ≠ state si blur/Tab omis).
    if (validateDateStr(v)) {
      const date = parseDateWithFormat(v, dateFormat);
      if (date) {
        setCurrentMonth(date);
        writeCivilDateToForm(format(date, "yyyy-MM-dd"));
      }
    }
  }

  /**
   * Après fermeture du popup : remettre le focus sur le champ date sauf si l'utilisateur
   * a déjà cliqué ailleurs (ex. libellé adjacent) — évite le « flicker » / vol de focus.
   */
  function scheduleRestoreFocusIfStillHere() {
    window.setTimeout(() => {
      const active = document.activeElement;
      if (
        active &&
        active !== document.body &&
        active !== inputRef.current &&
        !parentRef.current?.contains(active) &&
        !popoverWrapRef.current?.contains(active)
      ) {
        return;
      }
      inputRef.current?.focus();
      setIsFocused(true);
    }, 0);
  }

  function handleSelectData(selectedDate: Date) {
    const ymd = format(selectedDate, "yyyy-MM-dd");
    skipBlurCommitRef.current = true;
    setInputValue(format(selectedDate, dateFormat));
    writeCivilDateToForm(ymd);
    setIsOpenCalendar(false);
    if (onAfterSelect) {
      // Après paint : le libellé adjacent existe encore ; évite course avec blur/re-render.
      requestAnimationFrame(() => onAfterSelect());
    } else {
      scheduleRestoreFocusIfStillHere();
    }
  }

  function handleTodayClick() {
    const ymd = format(new Date(), "yyyy-MM-dd");
    skipBlurCommitRef.current = true;
    setCurrentMonth(new Date());
    setCurrentDate(new Date());
    setInputValue(format(new Date(), dateFormat));
    writeCivilDateToForm(ymd);
    setIsOpenCalendar(false);
    scheduleRestoreFocusIfStillHere();
  }

  function handleClearClick() {
    skipBlurCommitRef.current = true;
    setInputValue("");
    writeCivilDateToForm(null);
    setIsOpenCalendar(false);
    scheduleRestoreFocusIfStillHere();
  }

  function onInputFocus(e: FocusEvent<HTMLInputElement>) {
    if (readonly || formStateHandlers.getFormState() === U2000FormStateEnumState.READ) return;
    u2000SelectInputContentsOnFocus(e);
    setIsOpenCalendar(false);
    setIsFocused(true);
  }

  function commitInputBlurSideEffects(e: FocusEvent<HTMLInputElement>) {
    // Après sélection popup : ne pas re-commit ni re-valider (évite flash d'erreurs + écrasement).
    if (skipBlurCommitRef.current) {
      skipBlurCommitRef.current = false;
      return;
    }

    // Lire la valeur DOM (pas seulement le state React) — évite commit vide si désync.
    const raw = String(e.target.value ?? inputRef.current?.value ?? inputValue ?? "").trim();
    let ymdToCommit: string | null | undefined;
    if (validateDateStr(raw)) {
      const date = parseDateWithFormat(raw, dateFormat);
      if (date) {
        setCurrentMonth(date);
        ymdToCommit = format(date, "yyyy-MM-dd");
        setInputValue(format(date, dateFormat));
      }
    } else {
      ymdToCommit = null;
      setInputValue(raw);
    }

    // Différer setData / markTouched : un re-render synchrone pendant le blur vole le focus
    // du champ cliqué ensuite (libellé, autre date…) → il fallait recliquer.
    window.setTimeout(() => {
      if (ymdToCommit !== undefined) {
        writeCivilDateToForm(ymdToCommit);
      }
      if (formStateHandlers.markTouched) {
        formStateHandlers.markTouched(fieldName);
      } else if (formStateHandlers.onBlur) {
        formStateHandlers.onBlur(e, formStateHandlers.getData() as never);
      }
    }, 0);
  }

  function onInputBlur(e: FocusEvent<HTMLInputElement>) {
    setIsFocused(false);

    const closeAndCommit = () => {
      setIsOpenCalendar(false);
      commitInputBlurSideEffects(e);
    };

    if (popoverInPortal && isOpenCalendar) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const active = document.activeElement;
          if (popoverWrapRef.current?.contains(active) || parentRef.current?.contains(active)) {
            return;
          }
          closeAndCommit();
        });
      });
      return;
    }

    setIsOpenCalendar(false);
    commitInputBlurSideEffects(e);
  }

  useLayoutEffect(() => {
    if (!popoverInPortal || !isOpenCalendar || !parentRef.current) {
      setViewportFixed(null);
      return;
    }
    const updatePosition = () => {
      if (!parentRef.current) return;
      setViewportFixed(computeCalendarViewportPosition(parentRef.current, popupPosition));
    };
    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpenCalendar, popoverInPortal, popupPosition]);

  // Gestion du clic à l'extérieur pour fermer le calendrier
  useEffect(() => {
    if (!isOpenCalendar) return;

    function handleClickOutside(event: MouseEvent | globalThis.MouseEvent) {
      const t = event.target as Node;
      if (parentRef.current?.contains(t)) return;
      if (popoverWrapRef.current?.contains(t)) return;
      setIsOpenCalendar(false);
      setIsFocused(false);
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpenCalendar]);

  // Valeur du champ dans le formulaire (pour dépendance fiable et re-sync quand le parent préremplit)
  const formFieldValue = getNestedValue(formStateHandlers.getData(), fieldName) as string | undefined | null;

  useEffect(() => {
    if (!fieldName) return;
    const v = formFieldValue;
    if (v != null && String(v).trim() !== "") {
      const raw = String(v).trim();
      // Jour civil : préférer le préfixe yyyy-MM-dd (y compris si l’API a renvoyé un ISO
      // `…T00:00:00.000Z`) — éviter `new Date(iso)` + format local (ex. 2026-01-01 → 2025-12-31).
      const civilPrefix = raw.slice(0, 10);
      let date = /^\d{4}-\d{2}-\d{2}$/.test(civilPrefix)
        ? parseDateWithFormat(civilPrefix, "yyyy-MM-dd")
        : parseDateWithFormat(raw, "yyyy-MM-dd");
      if (!date) {
        date = new Date(raw);
        if (isNaN(date.getTime())) {
          setInputValue("");
          return;
        }
      }
      if (date && !isNaN(date.getTime())) {
        setInputValue(format(date, dateFormat));
      } else {
        setInputValue("");
      }
    } else {
      setInputValue("");
    }
  }, [fieldName, dateFormat, formFieldValue]);

  const calendarPopupCommon = {
    currentDate,
    currentMonth,
    mode: "single" as const,
    position: popupPosition,
    selectedValue: getNestedValue(formStateHandlers.getData(), fieldName),
    labels: calendarLabels,
    onSelectDate: handleSelectData,
    onTodayClick: handleTodayClick,
    onClearClick: handleClearClick,
    onMonthChange: setCurrentMonth,
    onClose: () => setIsOpenCalendar(false),
    zIndex: popupZIndex,
  };

  const showPortal = popoverInPortal && isOpenCalendar && viewportFixed != null && typeof document !== "undefined";

  const calendarField = (
    <div className={`relative max-w-full ${inlineEnd != null ? "w-auto min-w-44" : "w-full"}`} ref={parentRef}>
      <div className={getCustomCalendarClasses(formStateHandlers, fieldName, isFocused, readonly, compactGrid)}>
        <input
          ref={inputRef}
          type="text"
          id={fieldName}
          name={fieldName}
          value={inputValue}
          readOnly={readonly || formStateHandlers.getFormState() === U2000FormStateEnumState.READ}
          placeholder={placeholder || dateFormat}
          data-u2000-skip-grid-compact=""
          className="h-full min-h-0 flex-1 bg-transparent text-sm border-none outline-hidden min-w-0 py-0 leading-snug"
          onChange={(e: ChangeEvent<HTMLInputElement>) => handleInputChangeDate(e.target.value)}
          onFocus={onInputFocus}
          onBlur={onInputBlur}
          required={required}
          min={min}
          max={max}
          aria-invalid={!!(formStateHandlers.getTouched()[fieldName] && formStateHandlers.getErrors()[fieldName])}
          aria-describedby={formStateHandlers.getErrors()[fieldName] ? `${fieldName}-error` : undefined}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.preventDefault();
              setIsOpenCalendar(false);
            } else if (e.key === "F8" || (e.key === "Enter" && !isOpenCalendar)) {
              e.preventDefault();
              if (readonly || formStateHandlers.getFormState() === U2000FormStateEnumState.READ) return;
              setIsOpenCalendar(true);
            }
          }}
        />

        <button
          type="button"
          tabIndex={-1}
          onClick={handleCalendarClick}
          className={`flex h-full ${compactGrid ? "max-h-8" : "max-h-[46px]"} shrink-0 items-center justify-center px-2 text-gray-400 hover:text-gray-600 transition-colors duration-200 rounded-md hover:bg-gray-50`}
          disabled={readonly || formStateHandlers.getFormState() === U2000FormStateEnumState.READ}
          aria-label="Ouvrir le sélecteur de date"
        >
          <FaCalendarAlt className="w-4 h-4" />
        </button>
      </div>

      {!popoverInPortal && <U2000CalendarPopup isOpen={isOpenCalendar} {...calendarPopupCommon} />}
      {showPortal &&
        createPortal(
          <div ref={popoverWrapRef}>
            <U2000CalendarPopup {...calendarPopupCommon} isOpen viewportFixed={viewportFixed!} />
          </div>,
          document.body,
        )}
    </div>
  );

  return (
    <div className={`space-y-1.5 self-start ${inlineEnd != null ? "w-fit max-w-full" : "w-full max-w-full"}`}>
      <U2000Label label={label} required={required} htmlFor={fieldName} />

      {inlineEnd != null ? (
        <div className="flex w-fit max-w-full flex-row items-center gap-4">
          <div className="min-w-0 shrink-0">{calendarField}</div>
          <div className="flex shrink-0 items-center *:h-auto *:min-h-0">{inlineEnd}</div>
        </div>
      ) : (
        calendarField
      )}

      <U2000InvalidFeedback formStateHandlers={formStateHandlers} fieldName={fieldName} />
    </div>
  );
};

export default U2000InputCalendarCustom;
